import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { useSearchParams } from "react-router-dom";

// Layout
import AttendanceHeader from "./components/layout/AttendanceHeader";

// Filters
import FiltersPanel from "./components/filters/FiltersPanel";

// Table
import AttendanceTable from "./components/table/AttendanceTable";

// Overtime
import OvertimeDrawer from "./components/overtime/OvertimeDrawer";

// Modals
import HistoryModal from "./components/modals/HistoryModal";
import ImportModal from "./components/modals/ImportModal";
import AlertModal from "../../components/AlertModal";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";
import Pagination from "../../components/Pagination";

// Hooks
import useAttendance from "./hooks/useAttendance";
import useEmployees from "./hooks/useEmployees";
import useCompanyConfig from "./hooks/useCompanyConfig";
import useAttendanceActions from "./hooks/useAttendanceActions";
import useCSVImport from "./hooks/useCSVImport";
import usePermissions from "./hooks/usePermissions";
import useViewType from "./hooks/useViewType";
import useOvertimeApproval from "./hooks/useOvertimeApproval";
import useOvertimeRequests from "./hooks/useOvertimeRequests";
import { useEmployeeProfilePics } from "../(admin)employeemanagement/hooks/useEmployeeProfilePics";

// Constants
import translations from "./constants/translations";

// Styles
import { getCardStyle } from "./styles/attendanceStyles";

// Contexts / hooks
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useProfile } from "../../hooks/useProfile";

// ─── helpers ──────────────────────────────────────────────────────────────────

const getTodayStr = () => new Date().toISOString().split("T")[0];

const BASE_URL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "";

// Chunked loading: how many employees are loaded/rendered per "Show More" click.
// Keeps the first paint (LCP) fast and the backend load per request small.
const PAGE_SIZE = 30;

// ─── component ────────────────────────────────────────────────────────────────

/**
 * AttendanceManagement
 *
 * Props (injected by the parent page / router):
 *   theme       – "dark" | "light"
 *   language    – "en" | "ar"
 *   profile     – current user object from AuthContext
 *   showAlert   – (title, message, type, onConfirm?, requireConfirm?) => void
 */
const AttendanceManagement = ({ theme: themeProp, language, profile, showAlert: parentShowAlert }) => {
  const { theme: contextTheme } = useContext(ThemeContext) || { theme: "light" };
  const { language: contextLanguage } = useContext(LanguageContext) || { language: "en" };
  const { profile: contextProfile } = useProfile();

  const theme = themeProp || contextTheme || "light";
  const currentLanguage = language || contextLanguage || "en";
  const currentProfile = profile || contextProfile;

  const isRTL = currentLanguage === "ar";
  const t = translations[currentLanguage] || translations.en;

  // ── alert modal ────────────────────────────────────────────────────────────
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    showCancel: false,
    onConfirm: null,
  });

  const showAlert = useCallback((title, message, variant = 'info', onConfirm = null, showCancel = false) => {
    setAlertModal({
      open: true,
      title,
      message,
      variant,
      showCancel,
      onConfirm,
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertModal(prev => ({ ...prev, open: false }));
  }, []);

  const handleAlertConfirm = useCallback(() => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    closeAlert();
  }, [alertModal, closeAlert]);

  // ── view type ──────────────────────────────────────────────────────────────
  const { viewType, isAdminView } = useViewType();

  // ── permissions ────────────────────────────────────────────────────────────
  const { permissions, permissionsLoading, can } = usePermissions({ isAdminView, profile: currentProfile, BASE_URL, language: currentLanguage });

  // Feature gating applies only in Admin View (promoted user / admin-level).
  // `can()` already bypasses for admin/superadmin roles; manager view keeps
  // its historical allow-all behavior. (The previous `|| isAdminView` made
  // every flag constant-true, disabling admin-level gating entirely.)
  const canViewAttendance = can("view") || !isAdminView;
  const canRefreshAttendance = can("refresh") || !isAdminView;
  const canExportCsv = can("export_csv") || !isAdminView;
  const canExportTemplate = can("export_template") || !isAdminView;
  const canImportCsv = can("import_csv") || !isAdminView;
  const canScheduleTemplate = can("schedule_template") || !isAdminView;
  const canDefaultLocation = can("default_location") || !isAdminView;
  const canImportLocationSchedule = can("import_location_schedule") || !isAdminView;
  const canResetCheckIn = can("reset_check_in") || !isAdminView;
  const canResetCheckOut = can("reset_check_out") || !isAdminView;
  const canRevertOriginal = can("revert_original") || !isAdminView;
  const canApproveOvertime = can("approve_overtime") || !isAdminView;

  // 🔍 DEBUG: overtime button visibility — remove after fixing
  console.log("🔍 [OVERTIME DEBUG]", {
    viewType,
    isAdminView,
    canApproveOvertime,
    "can('approve_overtime')": can("approve_overtime"),
    "!isAdminView": !isAdminView,
    permissionsLoading,
    permissions,
    "permissions.features": permissions?.features,
    "profile.role": currentProfile?.role,
    "localStorage.role": localStorage.getItem("role"),
    "localStorage.managerViewType": localStorage.getItem("managerViewType"),
    "localStorage.viewType": localStorage.getItem("viewType"),
  });

  // ── filter / search state ──────────────────────────────────────────────────
  // Notification deep-link (?employee=&date=): open on the affected day with
  // the employee pre-filtered. Read once at mount via lazy initializers so
  // the very first data fetch already targets the right day.
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = searchParams.get("date");
    return /^\d{4}-\d{2}-\d{2}$/.test(d || "") ? d : getTodayStr();
  });
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("employee") || "");
  const [filterStatus, setFilterStatus] = useState("all");
  const [statusSearch, setStatusSearch] = useState("");
  const [filterWorkingCompany, setFilterWorkingCompany] = useState("all");
  const [companySearch, setCompanySearch] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [departmentSearch, setDepartmentSearch] = useState("");

  // ── overtime drawer ─────────────────────────────────────────────────────────
  const [showOvertimeDrawer, setShowOvertimeDrawer] = useState(false);

  // ── history modal ──────────────────────────────────────────────────────────
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  // ── company config hook ────────────────────────────────────────────────────
  const {
    companyConfigs,
    loadAllCompanyConfigs,
    getConfigForEmployee,
    getConfigForEmployeeOnDate,
  } = useCompanyConfig({ BASE_URL, language: currentLanguage, profile: currentProfile });

  // ── employees hook ─────────────────────────────────────────────────────────
  const { employees, loading: employeesLoading, loadEmployees, getEmployeeName } = useEmployees({
    BASE_URL,
    language: currentLanguage,
    viewType,
    profile: currentProfile,
  });

  // ── attendance hook ────────────────────────────────────────────────────────
  const {
    attendanceData,
    loading: attendanceLoading,
    attendanceDataRef,
    isRecalculating,
    setIsRecalculating,
    loadAttendance,
    refreshEmployeeRow,
  } = useAttendance({
    BASE_URL,
    language: currentLanguage,
    profile: currentProfile,
    viewType,
    getEmployeeName,
    loadAllCompanyConfigs,
  });

  // ── attendance actions hook ────────────────────────────────────────────────
  const { updateAttendancein, updateAttendanceout, revertAttendanceToOriginal } =
    useAttendanceActions({ BASE_URL, language: currentLanguage, getConfigForEmployeeOnDate });

  // ── overtime approval hook ─────────────────────────────────────────────────
  const { approveOvertime, rejectOvertime } = useOvertimeApproval({
    BASE_URL,
    language: currentLanguage,
    showAlert,
    t,
    viewType,
    onRefresh: () => refreshOvertimeRequests(),
  });

  // ── derive overtimeApprovalRequired from companyConfigs ───────────────────
  const overtimeFromConfigs = Object.values(companyConfigs).some(
    (cfg) => cfg?.current?.overtimeApprovalRequired === true
  );

  // Fallback: if companyConfigs hasn't loaded yet (e.g. attendance data not
  // yet available for a user account with admin level), fetch the default
  // company config once to determine if overtime approval is enabled.
  const [overtimeFallback, setOvertimeFallback] = useState(false);
  const overtimeFallbackFetched = useRef(false);

  useEffect(() => {
    if (overtimeFromConfigs || overtimeFallbackFetched.current) return;
    if (Object.keys(companyConfigs).length > 0) return; // configs loaded, just not overtime
    overtimeFallbackFetched.current = true;

    const clientName =
      currentProfile?.clientName || localStorage.getItem("clientName") || "";
    if (!clientName) return;

    const token = localStorage.getItem("token");
    fetch(`${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}`, {
      method: "GET",
      headers: {
        "Accept-Language": currentLanguage,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && (data?.data?.overtimeApprovalRequired === true || data?.data?.overtimeEnabled === true)) {
          setOvertimeFallback(true);
        }
      })
      .catch(() => { }); // non-critical
  }, [companyConfigs, overtimeFromConfigs, currentProfile, currentLanguage]);

  // Show overtime button when overtime is enabled OR when approval is required.
  // `overtimeEnabled` = overtime feature is turned on for the company.
  // `overtimeApprovalRequired` = overtime records need admin approval.
  // The button should appear in either case so admins can view/manage overtime.
  const overtimeEnabledInConfigs = Object.values(companyConfigs).some(
    (cfg) => cfg?.current?.overtimeEnabled === true || cfg?.current?.overtimeApprovalRequired === true
  );

  const overtimeApprovalRequired = overtimeFromConfigs || overtimeFallback || overtimeEnabledInConfigs;

  // 🔍 DEBUG: overtime config — remove after fixing
  console.log("🔍 [OVERTIME CONFIG DEBUG]", {
    overtimeApprovalRequired,
    overtimeFromConfigs,
    overtimeFallback,
    companyConfigsKeys: Object.keys(companyConfigs),
    companyConfigsValues: Object.values(companyConfigs).map(cfg => ({
      overtimeApprovalRequired: cfg?.current?.overtimeApprovalRequired,
      overtimeEnabled: cfg?.current?.overtimeEnabled,
    })),
  });

  // ── overtime requests (multi-day, loaded independently) ────────────────────
  const {
    overtimeRequests: drawerOvertimeRequests,
    overtimeLoading,
    overtimeDateRange,
    setOvertimeDateRange,
    loadOvertimeRequests,
    refreshOvertimeRequests,
    updateLocalRequest,
  } = useOvertimeRequests({
    BASE_URL,
    language: currentLanguage,
    viewType,
    getConfigForEmployee,
    overtimeApprovalRequired,
    isOpen: showOvertimeDrawer,
  });

  // ── CSV import hook ────────────────────────────────────────────────────────
  const {
    showImportModal,
    importPreview,
    importResult,
    importActiveTab,
    setImportActiveTab,
    isImporting,
    duplicateSelections,
    isResolvingDuplicates,
    handleImportFileChange,
    handleEditCell,
    handleDeleteRow,
    handleCloseImportModal,
    handleDuplicateSelection,
    handleUpdateDuplicate,
    handleResolveDuplicatesBulk,
    handleProcessImport,
  } = useCSVImport({
    BASE_URL,
    language: currentLanguage,
    profile: currentProfile,
    viewType,
    selectedDate,
    employees,
    loadAttendance,
    showAlert,
  });

  // ── initial load ───────────────────────────────────────────────────────────
  const initialised = useRef(false);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    loadEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── chunked loading (Show More) ────────────────────────────────────────────
  // Employees sorted by display name; only the first PAGE_SIZE are loaded at
  // first, matching the table's name ordering.
  const sortedEmployeeIds = useMemo(() => {
    const byName = [...employees].sort((a, b) => {
      const an = ((currentLanguage === "ar" ? a.arabicName || a.name : a.name || a.arabicName) || "").trim();
      const bn = ((currentLanguage === "ar" ? b.arabicName || b.name : b.name || b.arabicName) || "").trim();
      return an.localeCompare(bn);
    });
    return byName.map((e) => String(e.employeeId));
  }, [employees, currentLanguage]);

  // Employee metadata keyed by id, for building placeholder rows on pages whose
  // attendance hasn't been fetched yet (jump-to-page).
  const employeeById = useMemo(() => {
    const m = new Map();
    employees.forEach((e) => m.set(String(e.employeeId), e));
    return m;
  }, [employees]);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // ── reload attendance when date or employees change ────────────────────────
  useEffect(() => {
    setCurrentPage(1);
    if (employees.length > 0) {
      // Load only the first chunk — the backend syncs these 30 employees, not 200+
      loadAttendance(selectedDate, employees, 1, false, false, sortedEmployeeIds.slice(0, PAGE_SIZE));
    } else if (attendanceData.length === 0 && !employeesLoading) {
      // Genuine "no employee list" fallback (endpoint returned nothing) — and only
      // AFTER the employee list has finished loading. Without the !employeesLoading
      // guard this fired on initial mount while employees was momentarily [], doing
      // an unscoped full-roster load+sync (the "syncs everyone" bug).
      loadAttendance(selectedDate, employees);
    }
  }, [selectedDate, employees, employeesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ids already requested for the current date, so the missing-page loader
  // fetches each id at most once — even across the many re-renders that happen
  // while a request is in flight, and even for ids the backend returns no record
  // for (which would otherwise stay "missing" and re-fetch on every render).
  const requestedIdsRef = useRef(new Set());
  useEffect(() => {
    requestedIdsRef.current = new Set();
  }, [selectedDate]);

  // Fetch page data if missing
  useEffect(() => {
    if (searchTerm || filterStatus !== "all" || employees.length === 0) return;
    const pageIds = sortedEmployeeIds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const missingIds = pageIds.filter(
      (id) =>
        !attendanceDataRef.current.some((r) => String(r.employeeId) === String(id)) &&
        !requestedIdsRef.current.has(String(id))
    );
    if (missingIds.length === 0) return;
    missingIds.forEach((id) => requestedIdsRef.current.add(String(id)));
    setLoadingMore(true);
    loadAttendance(selectedDate, employees, 1, true, false, missingIds).finally(() => setLoadingMore(false));
  }, [currentPage, sortedEmployeeIds, searchTerm, filterStatus, employees, selectedDate, loadAttendance, attendanceDataRef]);

  // Load everything that isn't loaded yet (used by status filters and CSV export
  // so they always operate on the complete day, not just the visible chunk).
  // This is a READ-ONLY load (sync=false): it just fetches statuses so we can
  // determine which employees match a filter / will be exported. The matching
  // subset is then payroll-synced explicitly via syncEmployees() below.
  const loadRemainingEmployees = useCallback(async () => {
    const loadedIds = new Set(attendanceDataRef.current.map(r => String(r.employeeId)));
    const remaining = sortedEmployeeIds.filter(id => !loadedIds.has(String(id)));
    if (remaining.length === 0) return attendanceDataRef.current;
    setLoadingMore(true);
    try {
      const all = await loadAttendance(selectedDate, employees, 1, true, false, remaining, false);
      return all;
    } finally {
      setLoadingMore(false);
    }
  }, [sortedEmployeeIds, selectedDate, employees, loadAttendance, attendanceDataRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page when filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterWorkingCompany, filterDepartment]);

  // ── search across ALL employees (even unloaded chunks) ────────────────────
  // When the admin searches, matching employees that aren't loaded yet are
  // fetched on the fly (debounced) and merged into the table.
  useEffect(() => {
    if (!searchTerm || employees.length === 0) return;
    const handle = setTimeout(() => {
      const term = searchTerm.toLowerCase();
      const loadedIds = new Set(attendanceDataRef.current.map((r) => String(r.employeeId)));
      const matchedUnloaded = employees
        .filter((e) => {
          const idStr = String(e.employeeId);
          if (loadedIds.has(idStr)) return false;
          const name = (e.name || "").toLowerCase();
          const arName = (e.arabicName || "").toLowerCase();
          return name.includes(term) || arName.includes(term) || idStr.includes(searchTerm.trim());
        })
        .map((e) => String(e.employeeId))
        .slice(0, 100);

      if (matchedUnloaded.length > 0) {
        // Search pulling in employees from other pages: load AND sync them so a
        // searched-for employee's row is fresh when it appears. Scoped to the
        // matched-unloaded set.
        loadAttendance(selectedDate, employees, 1, true, false, matchedUnloaded);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Company / department are pure EMPLOYEE METADATA — every employee's values are
  // already known client-side (in `employees`), for the whole roster. So when one
  // is selected we can compute exactly which employees match and load ONLY that
  // subset (READ-ONLY, scoped), instead of pulling the entire roster.
  //
  // Status is NOT metadata — it comes from attendance — so a status filter never
  // triggers a load. Picking a status just narrows the rows already on screen;
  // an empty status simply shows nothing rather than fetching the whole roster to
  // discover it matched no one.
  useEffect(() => {
    const companyActive = filterWorkingCompany !== "all";
    const deptActive = filterDepartment !== "all";
    if (!companyActive && !deptActive) return;

    const loadedIds = new Set(
      attendanceDataRef.current.map((r) => String(r.employeeId))
    );
    const matchedUnloaded = employees
      .filter((e) => {
        if (companyActive && (e.workingCompany || "") !== filterWorkingCompany) return false;
        if (deptActive && (e.employeeType || "") !== filterDepartment) return false;
        return !loadedIds.has(String(e.employeeId));
      })
      .map((e) => String(e.employeeId));

    if (matchedUnloaded.length === 0) return;
    setLoadingMore(true);
    loadAttendance(selectedDate, employees, 1, true, false, matchedUnloaded, false)
      .finally(() => setLoadingMore(false));
  }, [filterWorkingCompany, filterDepartment, employees, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── force refresh (recalculate) ────────────────────────────────────────────
  const forceRefreshAttendance = useCallback(async () => {
    setIsRecalculating(true);
    try {
      // Explicit refresh: DO payroll-sync, but only the rows currently on screen
      // (loaded), never the whole roster. This is the one place the page syncs.
      const loadedIds = attendanceDataRef.current.map((r) => String(r.employeeId));
      if (loadedIds.length === 0) {
        setIsRecalculating(false);
        return; // nothing on screen to refresh — never fall back to a full-roster sync
      }
      await loadAttendance(
        selectedDate,
        employees,
        1,
        false,
        true,
        loadedIds,
        true // sync
      );
      showAlert?.(
        currentLanguage === "ar" ? "نجاح" : "Success",
        t.refreshSuccess,
        "success"
      );
    } catch {
      showAlert?.(
        currentLanguage === "ar" ? "خطأ" : "Error",
        t.refreshFailed,
        "error"
      );
    } finally {
      setIsRecalculating(false);
    }
  }, [selectedDate, employees, currentLanguage, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── targeted single-row refresh (used after reset/revert) ─────────────────
  // Only the affected employee is recomputed by the backend; falls back to a
  // full force refresh if the targeted request fails for any reason.
  const refreshSingleEmployee = useCallback(
    async (record) => {
      try {
        await refreshEmployeeRow(record.employeeId, record.date || selectedDate);
      } catch (err) {
        console.warn("Single-row refresh failed, falling back to full refresh:", err);
        await forceRefreshAttendance();
      }
    },
    [refreshEmployeeRow, selectedDate, forceRefreshAttendance]
  );

  // ── action wrappers ────────────────────────────────────────────────────────
  const handleUpdateIn = useCallback(
    (id, record, customTime) =>
      updateAttendancein(id, record, customTime, () => refreshSingleEmployee(record), showAlert, t),
    [updateAttendancein, refreshSingleEmployee, showAlert, t]
  );

  const handleUpdateOut = useCallback(
    (id, record, customTime) =>
      updateAttendanceout(id, record, customTime, () => refreshSingleEmployee(record), showAlert, t),
    [updateAttendanceout, refreshSingleEmployee, showAlert, t]
  );

  const handleRevert = useCallback(
    (record) =>
      revertAttendanceToOriginal(record, () => refreshSingleEmployee(record), showAlert, currentLanguage),
    [revertAttendanceToOriginal, refreshSingleEmployee, showAlert, currentLanguage]
  );

  // ── fetch employee history ─────────────────────────────────────────────────
  const fetchEmployeeHistory = useCallback(
    async (employeeId) => {
      if (!employeeId || employeeId === "") {
        showAlert?.(
          currentLanguage === "ar" ? "خطأ" : "Error",
          currentLanguage === "ar" ? "معرف الموظف غير صالح" : "Invalid employee ID",
          "error"
        );
        return;
      }
      try {
        const { apiFetch } = await import("../../context/AuthContext").catch(
          () => ({ apiFetch: fetch })
        );
        const month = selectedDate.slice(0, 7);
        const url = `${BASE_URL}/attendance/employee/month/${employeeId}?month=${month}`;
        const resp = await apiFetch(url, {
          method: "GET",
          headers: { "Accept-Language": currentLanguage },
        });
        if (!resp.ok) throw new Error("Failed to fetch history");
        const data = await resp.json();

        const records = data?.data || data?.history || data || [];
        setEmployeeHistory(Array.isArray(records) ? records : []);
        setSelectedEmployee(
          getEmployeeName(employeeId, attendanceData, currentLanguage) || String(employeeId)
        );
        setSelectedEmployeeId(String(employeeId));
        setShowHistoryModal(true);
      } catch (err) {
        console.error("fetchEmployeeHistory error:", err);
        showAlert?.(
          currentLanguage === "ar" ? "خطأ" : "Error",
          currentLanguage === "ar" ? "فشل تحميل سجل الموظف" : "Failed to load employee history",
          "error"
        );
      }
    },
    [selectedDate, currentLanguage, getEmployeeName, attendanceData, showAlert]
  );

  // ── utility to normalize status ────────────────────────────────────────────
  const normalizeStatus = (status) => {
    if (!status) return 'all';
    const normalized = String(status).toLowerCase().trim();
    if (normalized === 'onleave' || normalized === 'on_leave' || normalized === 'on leave') return 'on_leave';
    if (normalized === 'restday' || normalized === 'rest_day' || normalized === 'rest day') return 'rest_day';
    if (normalized.includes('halfday') || normalized.includes('half_day') || normalized.includes('half day')) return 'half_day';
    return normalized;
  };

  // Distinct working-company and department (employeeType) options for the filter
  // dropdowns — union of the loaded employees and any rows already fetched.
  const workingCompanyOptions = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => { if (e.workingCompany) set.add(e.workingCompany); });
    attendanceData.forEach((r) => { if (r.workingCompany) set.add(r.workingCompany); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [employees, attendanceData]);

  const departmentOptions = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => { if (e.employeeType) set.add(e.employeeType); });
    attendanceData.forEach((r) => { if (r.employeeType) set.add(r.employeeType); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [employees, attendanceData]);

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredAttendance = attendanceData.filter((record) => {
    const name =
      getEmployeeName(record.employeeId, attendanceData, currentLanguage) ||
      record.employeeName ||
      "";
    const matchesSearch =
      !searchTerm ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(record.employeeId).includes(searchTerm);

    const normalizedRecordStatus = normalizeStatus(record.status);
    const normalizedFilterStatus = normalizeStatus(filterStatus);
    const matchesStatus = normalizedFilterStatus === 'all' || normalizedRecordStatus === normalizedFilterStatus;

    const matchesCompany =
      filterWorkingCompany === 'all' || (record.workingCompany || '') === filterWorkingCompany;
    const matchesDepartment =
      filterDepartment === 'all' || (record.employeeType || '') === filterDepartment;

    return matchesSearch && matchesStatus && matchesCompany && matchesDepartment;
  });

  const sortedFilteredAttendance = useMemo(() => {
    return [...filteredAttendance].sort((a, b) => {
      const indexA = sortedEmployeeIds.indexOf(String(a.employeeId));
      const indexB = sortedEmployeeIds.indexOf(String(b.employeeId));
      return (indexA === -1 ? 999999 : indexA) - (indexB === -1 ? 999999 : indexB);
    });
  }, [filteredAttendance, sortedEmployeeIds]);

  const paginatedAttendance = useMemo(() => {
    const isFiltered =
      Boolean(searchTerm) ||
      filterStatus !== "all" ||
      filterWorkingCompany !== "all" ||
      filterDepartment !== "all";

    // Filtered: the universe is the loaded+filtered rows, so paginate those.
    if (isFiltered) {
      return sortedFilteredAttendance.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    }

    // Unfiltered: pages must map to the FULL roster order so page N always
    // shows the same 30 employees regardless of what's loaded. Look each id up
    // in the loaded rows; unloaded slots render as placeholders until the
    // missing-page loader (which fetches exactly this page's ids) fills them in.
    const pageIds = sortedEmployeeIds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const byId = new Map(attendanceData.map((r) => [String(r.employeeId), r]));
    return pageIds.map((id) => {
      const existing = byId.get(String(id));
      if (existing) return existing;
      const emp = employeeById.get(String(id));
      const nm =
        currentLanguage === "ar"
          ? emp?.arabicName || emp?.name
          : emp?.name || emp?.arabicName;
      return {
        id: `placeholder-${id}`,
        employeeId: id,
        employeeName: nm || String(id),
        clientName: "",
        workingCompany: emp?.workingCompany || null,
        employeeType: emp?.employeeType || null,
        date: selectedDate,
        checkIn: "…",
        checkOut: "…",
        hoursWorked: "-",
        status: "-",
        notes: "-",
        overTime: "-",
        underTime: "-",
        manuallyEdited: false,
        editedByIn: null,
        editedByOut: null,
        _placeholder: true,
      };
    });
  }, [
    sortedFilteredAttendance,
    currentPage,
    searchTerm,
    filterStatus,
    filterWorkingCompany,
    filterDepartment,
    sortedEmployeeIds,
    attendanceData,
    employeeById,
    currentLanguage,
    selectedDate,
  ]);

  // Payroll-sync a specific (small) set of employees via the syncing endpoint,
  // scoped to exactly the ids passed. Used for the visible page and for export.
  // forceRefresh=true so payroll is ACTUALLY recomputed for every id — without
  // it the backend only syncs rows that changed (new punch / no record yet) and
  // skips already-materialized rows, so the visible page wouldn't truly sync.
  const syncEmployees = useCallback(async (ids) => {
    const unique = [...new Set((ids || []).map(String))].filter(Boolean);
    if (unique.length === 0) return attendanceDataRef.current;
    return loadAttendance(selectedDate, employees, 1, true, true, unique, true);
  }, [selectedDate, employees, loadAttendance, attendanceDataRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── auto-sync the VISIBLE page ─────────────────────────────────────────────
  // Sync payroll only for the ~30 rows actually rendered on the current page —
  // NOT the whole filtered set (which can be the entire roster when almost
  // everyone is "absent"). Each employee is synced once per date; a ref tracks
  // who's been synced so the re-render caused by syncing doesn't loop.
  const syncedVisibleRef = useRef(new Set());
  useEffect(() => {
    // new date → forget what we've synced so the new day's page syncs
    syncedVisibleRef.current = new Set();
  }, [selectedDate]);
  useEffect(() => {
    const visibleIds = paginatedAttendance.map((r) => String(r.employeeId));
    const toSync = visibleIds.filter((id) => id && !syncedVisibleRef.current.has(id));
    if (toSync.length === 0) return;
    toSync.forEach((id) => syncedVisibleRef.current.add(id));
    syncEmployees(toSync);
  }, [paginatedAttendance, syncEmployees]);

  const totalPages = useMemo(() => {
    if (searchTerm || filterStatus !== 'all' || filterWorkingCompany !== 'all' || filterDepartment !== 'all') {
      return Math.ceil(sortedFilteredAttendance.length / PAGE_SIZE) || 1;
    }
    return Math.ceil(sortedEmployeeIds.length / PAGE_SIZE) || 1;
  }, [searchTerm, filterStatus, filterWorkingCompany, filterDepartment, sortedFilteredAttendance.length, sortedEmployeeIds.length]);


  // ── CSV export ─────────────────────────────────────────────────────────────
  const exportToCSV = useCallback(async () => {
    // Chunked loading: make sure the WHOLE day is loaded first so the export
    // never silently contains just the visible chunk.
    const allRows = await loadRemainingEmployees();
    const fullData = allRows && allRows.length ? allRows : attendanceDataRef.current;

    const exportSource = fullData.filter((record) => {
      const name =
        getEmployeeName(record.employeeId, fullData, currentLanguage) ||
        record.employeeName ||
        "";
      const matchesSearch =
        !searchTerm ||
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(record.employeeId).includes(searchTerm);
      const matchesStatus =
        normalizeStatus(filterStatus) === "all" ||
        normalizeStatus(record.status) === normalizeStatus(filterStatus);
      const matchesCompany =
        filterWorkingCompany === "all" || (record.workingCompany || "") === filterWorkingCompany;
      const matchesDepartment =
        filterDepartment === "all" || (record.employeeType || "") === filterDepartment;
      return matchesSearch && matchesStatus && matchesCompany && matchesDepartment;
    });

    if (!exportSource.length) {
      showAlert?.(
        currentLanguage === "ar" ? "تنبيه" : "Alert",
        t.noDataExport,
        "warning"
      );
      return;
    }

    // Export is an explicit action: payroll-sync exactly the rows being exported
    // (the filtered/searched set) BEFORE building the file so it reflects fresh data.
    await syncEmployees(exportSource.map((r) => r.employeeId));

    const headers = [
      "Employee Code", "Employee Name", "Date",
      "Check-In", "Check-Out", "Hours Worked",
      "Status", "Overtime", "Tardiness", "Client",
    ];

    const rows = exportSource.map((r) => {
      const employeeNameFromTable =
        getEmployeeName(r.employeeId, fullData, currentLanguage) ||
        r.employeeName ||
        (currentLanguage === "ar"
          ? r.employee?.arabicName || r.employee?.name_ar || r.employee?.sr
          : r.employee?.name || r.employee?.sr || r.employee?.fullName || r.employee?.englishName) ||
        "";

      return [
        r.employeeId,
        employeeNameFromTable,
        r.date,
        r.checkIn || "",
        r.checkOut || "",
        r.hoursWorked || "",
        r.status || "",
        r.overTime || "",
        r.underTime || "",
        r.clientName || "",
      ];
    });

    const csvContent =
      "\uFEFF" +
      [headers, ...rows]
        .map((row) =>
          row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance_${selectedDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [loadRemainingEmployees, syncEmployees, searchTerm, filterStatus, filterWorkingCompany, filterDepartment, selectedDate, currentLanguage, getEmployeeName, showAlert, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const overtimePendingCount = useMemo(() => {
    return drawerOvertimeRequests.filter((r) => {
      const overallStatus = r.overallStatus || "pending";
      return overallStatus !== "approved" && overallStatus !== "rejected";
    }).length;
  }, [drawerOvertimeRequests]);

  // ── initial page skeleton — only until the first successful load; later
  // `loading` toggles (refresh/date-change actions) won't re-trigger the skeleton.
  const isInitialLoading = employeesLoading || attendanceLoading;
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) setPageReady(true);
  }, [isInitialLoading]);

  // ── unauthorized guard ─────────────────────────────────────────────────────
  if (isAdminView && !permissionsLoading && !canViewAttendance) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          color: theme === "dark" ? "#f1f5f9" : "#1e293b",
          background: theme === "dark" ? "#0f172a" : "#f8fafc",
          padding: "24px",
          textAlign: "center",
        }}
      >
        <div>
          <h2>{currentLanguage === "ar" ? "غير مصرح" : "Unauthorized"}</h2>
          <p>{currentLanguage === "ar" ? "ليس لديك إذن لعرض صفحة الحضور." : "You do not have permission to view attendance."}</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isInitialLoading) {
    return (
      <div style={{ isolation: "auto" }}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات الحضور..." : "Loading attendance information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton — getCardStyle carries a large negative marginTop
            meant to tuck it under the topbar; neutralize it here so it
            doesn't slide up over the LoadingPill above it. */}
        <div style={{ ...getCardStyle(theme), marginTop: 0, marginBottom: "72px" }}>
          <SkeletonBox width="220px" height="30px" style={{ marginBottom: "12px" }} />
          <SkeletonBox width="300px" height="18px" />
        </div>

        {/* Filters panel skeleton */}
        <div style={getCardStyle(theme)}>
          <SkeletonBox width="180px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <SkeletonBox width="220px" height="42px" style={{ borderRadius: "10px" }} />
            <SkeletonBox width="180px" height="42px" style={{ borderRadius: "10px" }} />
            <SkeletonBox width="160px" height="42px" style={{ borderRadius: "10px" }} />
            <SkeletonBox width="140px" height="42px" style={{ borderRadius: "10px" }} />
          </div>
        </div>

        {/* Stats skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={{ ...getCardStyle(theme), marginTop: 0, marginBottom: 0 }} />
          ))}
        </div>

        {/* Table skeleton */}
        <div style={getCardStyle(theme)}>
          <SkeletonBox width="220px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonBox key={i} width="100%" height="48px" style={{ borderRadius: "10px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    /*
     * ✅ FIX: Removed any CSS properties that create a new stacking context on
     *   this root <div>. Stacking contexts are created by: transform, filter,
     *   opacity < 1, will-change, isolation, position + z-index, etc.
     *
     *   The original file had a bare <div> with no styles, which was already
     *   safe. We make this explicit by NOT adding position/z-index/transform/
     *   filter/opacity here, ensuring child portals (LocationScheduleImport
     *   modal, AlertModal) can freely escape to document.body without being
     *   clipped by a parent stacking context.
     */
    <div style={{ isolation: "auto" }}>
      {/* Page header */}
      <AttendanceHeader theme={theme} isRTL={isRTL} t={t} />

      {/* Filters + action buttons */}
      <FiltersPanel
        theme={theme}
        isRTL={isRTL}
        t={t}
        language={currentLanguage}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        statusSearch={statusSearch}
        setStatusSearch={setStatusSearch}
        workingCompanyOptions={workingCompanyOptions}
        filterWorkingCompany={filterWorkingCompany}
        setFilterWorkingCompany={setFilterWorkingCompany}
        companySearch={companySearch}
        setCompanySearch={setCompanySearch}
        departmentOptions={departmentOptions}
        filterDepartment={filterDepartment}
        setFilterDepartment={setFilterDepartment}
        departmentSearch={departmentSearch}
        setDepartmentSearch={setDepartmentSearch}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        filteredAttendance={filteredAttendance}
        attendanceData={attendanceData}
        totalEmployees={sortedEmployeeIds.length}
        isRecalculating={isRecalculating}
        canRefreshAttendance={canRefreshAttendance}
        canExportCsv={canExportCsv}
        canExportTemplate={canExportTemplate}
        canImportCsv={canImportCsv}
        canImportLocationSchedule={canImportLocationSchedule}
        canDefaultLocation={canDefaultLocation}
        canScheduleTemplate={canScheduleTemplate}
        canApproveOvertime={canApproveOvertime}
        overtimeApprovalRequired={overtimeApprovalRequired}
        forceRefreshAttendance={forceRefreshAttendance}
        exportToCSV={exportToCSV}
        handleImportFileChange={handleImportFileChange}
        overtimePendingCount={overtimePendingCount}
        overtimeTotalCount={drawerOvertimeRequests.length}
        onOvertimeClick={() => {
          // Set date range to today before opening drawer
          const now = new Date();
          const y = now.getFullYear();
          const m = now.getMonth();
          const day = now.getDate();
          const today = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

          setOvertimeDateRange({ start: today, end: today });
          setShowOvertimeDrawer(true);
        }}
      />

      {/* Attendance table */}
      <AttendanceTable
        theme={theme}
        isRTL={isRTL}
        t={t}
        language={currentLanguage}
        filteredAttendance={paginatedAttendance}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        viewType={viewType}
        companyConfigs={companyConfigs}
        canResetCheckIn={canResetCheckIn}
        canResetCheckOut={canResetCheckOut}
        canRevertOriginal={canRevertOriginal}
        canViewAttendance={canViewAttendance}
        canApproveOvertime={canApproveOvertime}
        overtimeApprovalRequired={overtimeApprovalRequired}
        onApproveOvertime={approveOvertime}
        onRejectOvertime={rejectOvertime}
        getConfigForEmployee={getConfigForEmployee}
        getEmployeeName={(id) => getEmployeeName(id, attendanceData, currentLanguage)}
        updateAttendancein={handleUpdateIn}
        updateAttendanceout={handleUpdateOut}
        revertAttendanceToOriginal={handleRevert}
        fetchEmployeeHistory={fetchEmployeeHistory}
        showAlert={showAlert}
        BASE_URL={BASE_URL}
      >
        {/* Pagination Controls */}
        {employees.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            theme={theme}
            disabled={loadingMore}
            bleed
          />
        )}
      </AttendanceTable>

      {/* Overtime Drawer */}
      <OvertimeDrawer
        isOpen={showOvertimeDrawer}
        onClose={() => setShowOvertimeDrawer(false)}
        canApprove={canApproveOvertime}
        requests={drawerOvertimeRequests}
        theme={theme}
        isRTL={isRTL}
        t={t}
        language={currentLanguage}
        onApprove={async (record) => {
          await approveOvertime(record);
          updateLocalRequest(record.id, { overallStatus: "approved" });
        }}
        onReject={async (record, rejectionReason) => {
          await rejectOvertime(record, rejectionReason);
          updateLocalRequest(record.id, { overallStatus: "rejected" });
        }}
        getConfigForEmployee={getConfigForEmployee}
        dateRange={overtimeDateRange}
        onDateRangeChange={setOvertimeDateRange}
        overtimeLoading={overtimeLoading}
        refreshOvertimeRequests={refreshOvertimeRequests}
      />

      {/* History modal */}
      <HistoryModal
        theme={theme}
        isRTL={isRTL}
        t={t}
        language={currentLanguage}
        showHistoryModal={showHistoryModal}
        setShowHistoryModal={setShowHistoryModal}
        employeeHistory={employeeHistory}
        selectedEmployee={selectedEmployee}
        selectedEmployeeId={selectedEmployeeId}
      />

      {/* CSV import modal */}
      <ImportModal
        theme={theme}
        language={currentLanguage}
        isRTL={isRTL}
        showImportModal={showImportModal}
        importPreview={importPreview}
        importResult={importResult}
        importActiveTab={importActiveTab}
        setImportActiveTab={setImportActiveTab}
        isImporting={isImporting}
        duplicateSelections={duplicateSelections}
        isResolvingDuplicates={isResolvingDuplicates}
        onEditCell={handleEditCell}
        onDeleteRow={handleDeleteRow}
        onClose={handleCloseImportModal}
        onDuplicateSelect={handleDuplicateSelection}
        onUpdateDuplicate={handleUpdateDuplicate}
        onResolveBulk={handleResolveDuplicatesBulk}
        onProcessImport={handleProcessImport}
      />

      {/* Alert modal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={handleAlertConfirm}
        onClose={closeAlert}
        theme={theme}
        language={currentLanguage}
      />
    </div>
  );
};

export default AttendanceManagement;